import React, { useState, useEffect, useCallback } from 'react';
import { Heart, Plus, Search, Trash2, Gift, TrendingUp, Users, Star, Phone, Mail, Award, History, Settings, Save, X, ChevronDown } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fp = (p) => `${p?.toLocaleString() || 0}`;

export const LoyaltyTab = () => {
  const [customers, setCustomers] = useState([]);
  const [stats, setStats] = useState(null);
  const [config, setConfig] = useState(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('dashboard'); // dashboard | customers | config | detail
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newCustomer, setNewCustomer] = useState({ name: '', phone: '', email: '', notes: '' });
  const [pointsInput, setPointsInput] = useState('');
  const [pointsReason, setPointsReason] = useState('');
  const [pointsAction, setPointsAction] = useState(null); // {customerId, type: 'earn'|'redeem'}

  const fetchData = useCallback(async () => {
    try {
      const [custRes, statsRes, configRes] = await Promise.allSettled([
        axios.get(`${API}/admin/loyalty/customers`, { params: { search } }),
        axios.get(`${API}/admin/loyalty/stats`),
        axios.get(`${API}/admin/loyalty/config`),
      ]);
      if (custRes.status === 'fulfilled') setCustomers(custRes.value.data);
      if (statsRes.status === 'fulfilled') setStats(statsRes.value.data);
      if (configRes.status === 'fulfilled') setConfig(configRes.value.data);
    } catch {} finally { setLoading(false); }
  }, [search]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const addCustomer = async () => {
    if (!newCustomer.name || !newCustomer.phone) return toast.error('Nom et telephone requis');
    try {
      await axios.post(`${API}/admin/loyalty/customers`, newCustomer);
      toast.success('Client ajoute');
      setNewCustomer({ name: '', phone: '', email: '', notes: '' });
      setShowAddModal(false);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const deleteCustomer = async (id) => {
    if (!window.confirm('Supprimer ce client fidele ?')) return;
    try {
      await axios.delete(`${API}/admin/loyalty/customers/${id}`);
      toast.success('Client supprime');
      setSelectedCustomer(null);
      setView('customers');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const viewCustomerDetail = async (id) => {
    try {
      const res = await axios.get(`${API}/admin/loyalty/customers/${id}`);
      setSelectedCustomer(res.data);
      setView('detail');
    } catch { toast.error('Erreur chargement'); }
  };

  const handlePoints = async () => {
    if (!pointsAction || !pointsInput) return;
    const pts = parseInt(pointsInput);
    if (!pts || pts <= 0) return toast.error('Points invalides');
    try {
      const endpoint = pointsAction.type === 'earn' ? 'earn' : 'redeem';
      await axios.post(`${API}/admin/loyalty/customers/${pointsAction.customerId}/${endpoint}`, {
        points: pts, reason: pointsReason || (pointsAction.type === 'earn' ? 'Achat' : 'Recompense'),
      });
      toast.success(pointsAction.type === 'earn' ? `+${pts} points` : `${pts} points utilises`);
      setPointsAction(null); setPointsInput(''); setPointsReason('');
      viewCustomerDetail(pointsAction.customerId);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const saveConfig = async () => {
    try {
      await axios.post(`${API}/admin/loyalty/config`, config);
      toast.success('Configuration sauvegardee');
    } catch { toast.error('Erreur'); }
  };

  if (loading) return <div className="flex items-center justify-center py-16"><div className="w-6 h-6 border-3 border-[#0A422D]/30 border-t-[#0A422D] rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-4" data-testid="loyalty-tab">
      {/* Header */}
      <div className="bg-white rounded-2xl p-4 border border-[#E5DCD3] flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <Heart className="w-5 h-5 text-red-500" />
          <h3 className="font-bold text-[#2D241D]">Programme de fidelite</h3>
        </div>
        <div className="flex gap-2">
          {['dashboard', 'customers', 'config'].map(v => (
            <button key={v} onClick={() => setView(v)}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${view === v ? 'bg-[#0A422D] text-white' : 'bg-[#F3EDE5] text-[#6E5E52] hover:bg-[#E5DCD3]'}`}
              data-testid={`loyalty-view-${v}`}>
              {v === 'dashboard' ? 'Tableau de bord' : v === 'customers' ? 'Clients' : 'Configuration'}
            </button>
          ))}
          <Button onClick={() => setShowAddModal(true)} className="bg-[#D4A853] hover:bg-[#C49A48] text-black rounded-xl gap-1 text-xs" data-testid="loyalty-add-btn">
            <Plus className="w-3.5 h-3.5" /> Nouveau client
          </Button>
        </div>
      </div>

      {/* ─── DASHBOARD ─── */}
      {view === 'dashboard' && stats && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-white rounded-2xl p-4 border border-[#E5DCD3] text-center">
              <Users className="w-5 h-5 mx-auto mb-1 text-[#0A422D]" />
              <p className="text-2xl font-black text-[#2D241D]" data-testid="loyalty-total-customers">{stats.total_customers}</p>
              <p className="text-xs text-[#6E5E52]">Clients fideles</p>
            </div>
            <div className="bg-white rounded-2xl p-4 border border-[#E5DCD3] text-center">
              <Star className="w-5 h-5 mx-auto mb-1 text-[#D4A853]" />
              <p className="text-2xl font-black text-[#D4A853]">{fp(stats.total_points_active)}</p>
              <p className="text-xs text-[#6E5E52]">Points actifs</p>
            </div>
            <div className="bg-white rounded-2xl p-4 border border-[#E5DCD3] text-center">
              <Gift className="w-5 h-5 mx-auto mb-1 text-[#E16833]" />
              <p className="text-2xl font-black text-[#E16833]">{stats.total_rewards_used}</p>
              <p className="text-xs text-[#6E5E52]">Recompenses utilisees</p>
            </div>
            <div className="bg-white rounded-2xl p-4 border border-[#E5DCD3] text-center">
              <TrendingUp className="w-5 h-5 mx-auto mb-1 text-[#2D6A4F]" />
              <p className="text-2xl font-black text-[#2D6A4F]">{fp(stats.total_spent)}</p>
              <p className="text-xs text-[#6E5E52]">Total depense</p>
            </div>
          </div>

          {/* Top customers */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-white rounded-2xl border border-[#E5DCD3] overflow-hidden">
              <div className="px-4 py-3 bg-[#FAF7F2] border-b border-[#E5DCD3]">
                <h4 className="text-sm font-bold text-[#2D241D] flex items-center gap-2"><Award className="w-4 h-4 text-[#D4A853]" /> Top clients</h4>
              </div>
              <div className="divide-y divide-[#F3EDE5]">
                {(stats.top_customers || []).slice(0, 8).map((c, i) => (
                  <div key={c.id} className="px-4 py-2.5 flex items-center gap-3 cursor-pointer hover:bg-[#FAF7F2]"
                    onClick={() => viewCustomerDetail(c.id)} data-testid={`loyalty-top-${c.id}`}>
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${i < 3 ? 'bg-[#D4A853] text-black' : 'bg-[#F3EDE5] text-[#6E5E52]'}`}>{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#2D241D] truncate">{c.name}</p>
                      <p className="text-[10px] text-[#6E5E52]">{c.phone}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-[#D4A853]">{fp(c.points)} pts</p>
                      <p className="text-[10px] text-[#6E5E52]">{c.total_visits} visites</p>
                    </div>
                  </div>
                ))}
                {(!stats.top_customers || stats.top_customers.length === 0) && (
                  <p className="text-center text-xs text-[#6E5E52] py-6">Aucun client inscrit</p>
                )}
              </div>
            </div>

            {/* Recent transactions */}
            <div className="bg-white rounded-2xl border border-[#E5DCD3] overflow-hidden">
              <div className="px-4 py-3 bg-[#FAF7F2] border-b border-[#E5DCD3]">
                <h4 className="text-sm font-bold text-[#2D241D] flex items-center gap-2"><History className="w-4 h-4 text-[#6E5E52]" /> Transactions recentes</h4>
              </div>
              <div className="divide-y divide-[#F3EDE5] max-h-[350px] overflow-y-auto">
                {(stats.recent_transactions || []).map(tx => (
                  <div key={tx.id} className="px-4 py-2 flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${tx.type === 'earn' ? 'bg-green-500' : 'bg-red-500'}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-[#2D241D]">{tx.reason}</p>
                      <p className="text-[10px] text-[#6E5E52]">{new Date(tx.created_at).toLocaleString('fr-FR')}</p>
                    </div>
                    <span className={`text-sm font-bold ${tx.type === 'earn' ? 'text-green-600' : 'text-red-500'}`}>
                      {tx.type === 'earn' ? '+' : ''}{tx.points} pts
                    </span>
                  </div>
                ))}
                {(!stats.recent_transactions || stats.recent_transactions.length === 0) && (
                  <p className="text-center text-xs text-[#6E5E52] py-6">Aucune transaction</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── CUSTOMERS LIST ─── */}
      {view === 'customers' && (
        <div className="space-y-3">
          <div className="bg-white rounded-2xl p-3 border border-[#E5DCD3] flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6E5E52]" />
              <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher par nom, telephone..."
                className="pl-9 rounded-lg" data-testid="loyalty-search" />
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-[#E5DCD3] overflow-hidden">
            <div className="divide-y divide-[#F3EDE5]">
              {customers.map(c => (
                <div key={c.id} className="px-4 py-3 flex items-center gap-3 hover:bg-[#FAF7F2] cursor-pointer"
                  onClick={() => viewCustomerDetail(c.id)} data-testid={`loyalty-customer-${c.id}`}>
                  <div className="w-10 h-10 bg-[#D4A853]/10 rounded-full flex items-center justify-center">
                    <Heart className="w-5 h-5 text-[#D4A853]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-[#2D241D]">{c.name}</p>
                    <p className="text-xs text-[#6E5E52] flex items-center gap-2">
                      <Phone className="w-3 h-3" /> {c.phone}
                      {c.email && <><Mail className="w-3 h-3 ml-1" /> {c.email}</>}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-black text-[#D4A853]">{fp(c.points)}<span className="text-xs font-normal text-[#6E5E52]"> pts</span></p>
                    <p className="text-[10px] text-[#6E5E52]">{c.total_visits} visites | {fp(c.total_spent)} depense</p>
                  </div>
                </div>
              ))}
              {customers.length === 0 && (
                <div className="text-center py-12 text-[#6E5E52]">
                  <Heart className="w-12 h-12 mx-auto mb-3 opacity-20" />
                  <p className="text-sm">{search ? 'Aucun resultat' : 'Aucun client fidele inscrit'}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ─── CUSTOMER DETAIL ─── */}
      {view === 'detail' && selectedCustomer && (
        <div className="space-y-4">
          <button onClick={() => setView('customers')} className="text-xs text-[#6E5E52] hover:underline" data-testid="loyalty-back-btn">
            Retour a la liste
          </button>
          <div className="bg-white rounded-2xl border border-[#E5DCD3] p-4">
            <div className="flex items-start justify-between">
              <div>
                <h4 className="text-xl font-black text-[#2D241D]" data-testid="loyalty-detail-name">{selectedCustomer.name}</h4>
                <p className="text-sm text-[#6E5E52] flex items-center gap-2 mt-1">
                  <Phone className="w-3.5 h-3.5" /> {selectedCustomer.phone}
                  {selectedCustomer.email && <><Mail className="w-3.5 h-3.5 ml-2" /> {selectedCustomer.email}</>}
                </p>
                <p className="text-xs text-[#6E5E52] mt-1">Inscrit le {new Date(selectedCustomer.created_at).toLocaleDateString('fr-FR')}</p>
              </div>
              <div className="text-right">
                <p className="text-3xl font-black text-[#D4A853]" data-testid="loyalty-detail-points">{fp(selectedCustomer.points)}</p>
                <p className="text-xs text-[#6E5E52]">points</p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3 mt-4">
              <div className="bg-[#FAF7F2] rounded-xl p-3 text-center">
                <p className="text-lg font-bold text-[#2D241D]">{selectedCustomer.total_visits}</p>
                <p className="text-xs text-[#6E5E52]">Visites</p>
              </div>
              <div className="bg-[#FAF7F2] rounded-xl p-3 text-center">
                <p className="text-lg font-bold text-[#2D241D]">{fp(selectedCustomer.total_spent)}</p>
                <p className="text-xs text-[#6E5E52]">Total depense</p>
              </div>
              <div className="bg-[#FAF7F2] rounded-xl p-3 text-center">
                <p className="text-lg font-bold text-[#E16833]">{selectedCustomer.rewards_used}</p>
                <p className="text-xs text-[#6E5E52]">Recompenses</p>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <Button onClick={() => { setPointsAction({ customerId: selectedCustomer.id, type: 'earn' }); setPointsInput(''); setPointsReason(''); }}
                className="bg-[#2D6A4F] hover:bg-[#1B4332] text-white rounded-xl gap-1 flex-1" data-testid="loyalty-earn-btn">
                <Plus className="w-4 h-4" /> Ajouter points
              </Button>
              <Button onClick={() => { setPointsAction({ customerId: selectedCustomer.id, type: 'redeem' }); setPointsInput(''); setPointsReason(''); }}
                className="bg-[#E16833] hover:bg-[#C95A2A] text-white rounded-xl gap-1 flex-1" data-testid="loyalty-redeem-btn">
                <Gift className="w-4 h-4" /> Utiliser points
              </Button>
              <Button onClick={() => deleteCustomer(selectedCustomer.id)} variant="outline"
                className="rounded-xl text-red-500 hover:bg-red-50" data-testid="loyalty-delete-btn">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Rewards available */}
          {config && config.rewards && (
            <div className="bg-white rounded-2xl border border-[#E5DCD3] overflow-hidden">
              <div className="px-4 py-3 bg-[#FAF7F2] border-b border-[#E5DCD3]">
                <h4 className="text-sm font-bold text-[#2D241D] flex items-center gap-2"><Gift className="w-4 h-4 text-[#D4A853]" /> Recompenses disponibles</h4>
              </div>
              <div className="p-3 flex flex-wrap gap-2">
                {config.rewards.map((r, i) => {
                  const canRedeem = selectedCustomer.points >= r.threshold;
                  return (
                    <div key={i} className={`px-3 py-2 rounded-xl border text-center ${canRedeem ? 'border-[#D4A853] bg-[#D4A853]/5' : 'border-[#E5DCD3] opacity-40'}`}>
                      <p className="text-lg font-bold text-[#D4A853]">{r.label}</p>
                      <p className="text-[10px] text-[#6E5E52]">{r.threshold} pts requis</p>
                      {canRedeem && (
                        <button onClick={() => { setPointsAction({ customerId: selectedCustomer.id, type: 'redeem' }); setPointsInput(String(r.threshold)); setPointsReason(r.label); }}
                          className="mt-1 text-[10px] bg-[#D4A853] text-black px-2 py-0.5 rounded-full font-bold" data-testid={`loyalty-claim-${i}`}>
                          Appliquer
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Transaction history */}
          {selectedCustomer.transactions && (
            <div className="bg-white rounded-2xl border border-[#E5DCD3] overflow-hidden">
              <div className="px-4 py-3 bg-[#FAF7F2] border-b border-[#E5DCD3]">
                <h4 className="text-sm font-bold text-[#2D241D] flex items-center gap-2"><History className="w-4 h-4" /> Historique</h4>
              </div>
              <div className="divide-y divide-[#F3EDE5] max-h-[300px] overflow-y-auto">
                {selectedCustomer.transactions.map(tx => (
                  <div key={tx.id} className="px-4 py-2 flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${tx.type === 'earn' ? 'bg-green-500' : 'bg-red-500'}`} />
                    <div className="flex-1">
                      <p className="text-xs text-[#2D241D]">{tx.reason}</p>
                      <p className="text-[10px] text-[#6E5E52]">{new Date(tx.created_at).toLocaleString('fr-FR')}</p>
                    </div>
                    <span className={`text-sm font-bold ${tx.type === 'earn' ? 'text-green-600' : 'text-red-500'}`}>
                      {tx.points > 0 ? '+' : ''}{tx.points}
                    </span>
                  </div>
                ))}
                {(!selectedCustomer.transactions || selectedCustomer.transactions.length === 0) && (
                  <p className="text-center text-xs text-[#6E5E52] py-4">Aucune transaction</p>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ─── CONFIG ─── */}
      {view === 'config' && config && (
        <div className="space-y-4">
          <div className="bg-white rounded-2xl border border-[#E5DCD3] overflow-hidden">
            <div className="px-4 py-3 bg-[#FAF7F2] border-b border-[#E5DCD3]">
              <h4 className="text-sm font-bold text-[#2D241D] flex items-center gap-2"><Settings className="w-4 h-4" /> Regles du programme</h4>
            </div>
            <div className="p-4 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-[#6E5E52] mb-1 block">Points par unite</label>
                  <Input type="number" value={config.points_per_unit}
                    onChange={e => setConfig(prev => ({ ...prev, points_per_unit: parseInt(e.target.value) || 1 }))}
                    className="rounded-lg" data-testid="loyalty-config-ppu" />
                </div>
                <div>
                  <label className="text-xs font-medium text-[#6E5E52] mb-1 block">Depense par point (THB)</label>
                  <Input type="number" value={config.currency_per_point}
                    onChange={e => setConfig(prev => ({ ...prev, currency_per_point: parseFloat(e.target.value) || 100 }))}
                    className="rounded-lg" data-testid="loyalty-config-cpp" />
                  <p className="text-[10px] text-[#6E5E52] mt-0.5">1 point pour chaque {config.currency_per_point} depense</p>
                </div>
              </div>

              <div>
                <label className="text-xs font-medium text-[#6E5E52] mb-2 block">Paliers de recompense</label>
                <div className="space-y-2">
                  {(config.rewards || []).map((r, i) => (
                    <div key={i} className="flex items-center gap-2 bg-[#FAF7F2] rounded-lg p-2">
                      <Input type="number" value={r.threshold} placeholder="Points"
                        onChange={e => {
                          const newRewards = [...config.rewards];
                          newRewards[i] = { ...newRewards[i], threshold: parseInt(e.target.value) || 0 };
                          setConfig(prev => ({ ...prev, rewards: newRewards }));
                        }}
                        className="w-20 h-8 text-xs rounded-lg" />
                      <span className="text-xs text-[#6E5E52]">pts =</span>
                      <select value={r.type} onChange={e => {
                        const newRewards = [...config.rewards];
                        newRewards[i] = { ...newRewards[i], type: e.target.value };
                        setConfig(prev => ({ ...prev, rewards: newRewards }));
                      }}
                        className="h-8 px-2 rounded-lg border border-[#E5DCD3] text-xs">
                        <option value="percent">% remise</option>
                        <option value="fixed">Montant fixe</option>
                      </select>
                      <Input type="number" value={r.value} placeholder="Valeur"
                        onChange={e => {
                          const newRewards = [...config.rewards];
                          newRewards[i] = { ...newRewards[i], value: parseFloat(e.target.value) || 0, label: r.type === 'percent' ? `-${e.target.value}%` : `-${e.target.value}` };
                          setConfig(prev => ({ ...prev, rewards: newRewards }));
                        }}
                        className="w-20 h-8 text-xs rounded-lg" />
                      <span className="text-xs font-bold text-[#D4A853] w-16">{r.label}</span>
                      <button onClick={() => {
                        const newRewards = config.rewards.filter((_, idx) => idx !== i);
                        setConfig(prev => ({ ...prev, rewards: newRewards }));
                      }} className="text-red-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  ))}
                  <button onClick={() => setConfig(prev => ({
                    ...prev, rewards: [...(prev.rewards || []), { threshold: 0, type: 'percent', value: 0, label: '' }]
                  }))} className="text-xs text-[#0A422D] font-bold hover:underline flex items-center gap-1">
                    <Plus className="w-3 h-3" /> Ajouter un palier
                  </button>
                </div>
              </div>
              <Button onClick={saveConfig} className="bg-[#0A422D] hover:bg-[#083323] text-white rounded-xl gap-2" data-testid="loyalty-save-config">
                <Save className="w-4 h-4" /> Sauvegarder
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ ADD CUSTOMER MODAL ═══ */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center" onClick={() => setShowAddModal(false)}>
          <div className="bg-white rounded-3xl w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="loyalty-add-modal">
            <div className="bg-[#D4A853] text-black p-4 rounded-t-3xl flex items-center justify-between">
              <h3 className="font-bold">Nouveau client fidele</h3>
              <button onClick={() => setShowAddModal(false)} className="w-8 h-8 rounded-full bg-black/10 flex items-center justify-center"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="text-xs font-medium text-[#6E5E52] mb-1 block">Nom *</label>
                <Input value={newCustomer.name} onChange={e => setNewCustomer(p => ({ ...p, name: e.target.value }))}
                  placeholder="Nom du client" className="rounded-lg" data-testid="loyalty-new-name" />
              </div>
              <div>
                <label className="text-xs font-medium text-[#6E5E52] mb-1 block">Telephone *</label>
                <Input value={newCustomer.phone} onChange={e => setNewCustomer(p => ({ ...p, phone: e.target.value }))}
                  placeholder="+66 812345678" className="rounded-lg" data-testid="loyalty-new-phone" />
              </div>
              <div>
                <label className="text-xs font-medium text-[#6E5E52] mb-1 block">Email</label>
                <Input value={newCustomer.email} onChange={e => setNewCustomer(p => ({ ...p, email: e.target.value }))}
                  placeholder="email@example.com" className="rounded-lg" data-testid="loyalty-new-email" />
              </div>
              <div>
                <label className="text-xs font-medium text-[#6E5E52] mb-1 block">Notes</label>
                <Input value={newCustomer.notes} onChange={e => setNewCustomer(p => ({ ...p, notes: e.target.value }))}
                  placeholder="Allergies, preferences..." className="rounded-lg" data-testid="loyalty-new-notes" />
              </div>
              <Button onClick={addCustomer} className="w-full bg-[#D4A853] hover:bg-[#C49A48] text-black rounded-xl font-bold" data-testid="loyalty-save-new">
                Inscrire le client
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ POINTS MODAL ═══ */}
      {pointsAction && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center" onClick={() => setPointsAction(null)}>
          <div className="bg-white rounded-3xl w-full max-w-sm" onClick={e => e.stopPropagation()} data-testid="loyalty-points-modal">
            <div className={`${pointsAction.type === 'earn' ? 'bg-[#2D6A4F]' : 'bg-[#E16833]'} text-white p-4 rounded-t-3xl flex items-center justify-between`}>
              <h3 className="font-bold">{pointsAction.type === 'earn' ? 'Ajouter des points' : 'Utiliser des points'}</h3>
              <button onClick={() => setPointsAction(null)} className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="text-xs font-medium text-[#6E5E52] mb-1 block">Points</label>
                <Input type="number" value={pointsInput} onChange={e => setPointsInput(e.target.value)}
                  placeholder="Nombre de points" className="rounded-lg text-lg h-12 text-center font-bold" autoFocus
                  data-testid="loyalty-points-input" />
              </div>
              <div>
                <label className="text-xs font-medium text-[#6E5E52] mb-1 block">Raison</label>
                <Input value={pointsReason} onChange={e => setPointsReason(e.target.value)}
                  placeholder={pointsAction.type === 'earn' ? 'Achat, bonus...' : 'Recompense, cadeau...'}
                  className="rounded-lg" data-testid="loyalty-points-reason" />
              </div>
              <Button onClick={handlePoints} disabled={!pointsInput}
                className={`w-full rounded-xl font-bold ${pointsAction.type === 'earn' ? 'bg-[#2D6A4F] hover:bg-[#1B4332]' : 'bg-[#E16833] hover:bg-[#C95A2A]'} text-white`}
                data-testid="loyalty-points-confirm">
                {pointsAction.type === 'earn' ? 'Ajouter' : 'Utiliser'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
